<template>
  <FreeBottomSheet
    :persistent="isDraftStateHasChanges"
    :name="name"
    @onOpen="init"
    @onTryClose="emit('onTryToClose')"
  >
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <template #default>
          <h2 class="text-text-neutral-primary text-base font-medium">
            {{ t('ancillaries.seatMap.pageTitle') }}
          </h2>
        </template>
        <template #rightAction>
          <FreeCloseButton @click="hide" />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div class="flex min-h-[80vh] flex-grow flex-col justify-between">
        <div class="flex h-full flex-grow flex-col space-y-4 px-4">
          <div
            v-if="flight.segments.length > 1"
            ref="flightSelectBlockRef"
            class="no-scrollbar flex w-full space-x-3 overflow-scroll"
          >
            <FlightSelectButton
              v-for="(segment, idx) in flight.segments"
              :key="segment.flight_number"
              :is-active="seatMap.activeSegmentIdx.value === idx"
              :has-committed-seats="hasSegmentCommittedSeats(idx)"
              @click="seatMap.selectSegment(Number(idx))"
            >
              <p
                :class="
                  seatMap.activeSegmentIdx.value === idx
                    ? 'text-static-white'
                    : 'text-text-neutral-primary'
                "
              >
                {{
                  t('ancillaries.seatMap.segmentTitle', {
                    segmentNumber: idx + 1,
                  })
                }}
              </p>
            </FlightSelectButton>
          </div>
          <SegmentDetails
            v-if="seatMap.activeSegment.value"
            :segment="seatMap.activeSegment.value"
            :passengers-amount="passengersKeys.length"
          />
          <div
            v-if="seatMap.activeSegmentQuery.value.isLoading"
            class="flex flex-1 flex-col justify-center"
          >
            <SeatMapLoader />
          </div>
          <SeatMapSegmentError
            v-else-if="seatMap.activeSegmentQuery.value.isError"
            @onRefetch="seatMap.activeSegmentQuery.value.refetch"
          >
            <template #default>
              <FreeButton
                type="button"
                class="green-main-button"
                @click="skipSegment"
              >
                {{ skipSegmentButtonText }}
              </FreeButton>
            </template>
          </SeatMapSegmentError>
          <div
            v-else-if="seatMap.mappedSeatMap.value"
            class="space-y-2 pb-[178px]"
          >
            <SeatMapLegend
              v-if="seatMap.seatMapLegend.value"
              :seat-map-legend="seatMap.seatMapLegend.value"
            />
            <SeatMapSchema
              :passengers-keys="passengersKeys"
              :active-passenger-key="seatMap.activePassengerKey.value"
              :seat-map-legend="seatMap.seatMapLegend.value"
              :max-seats-in-row="seatMap.maxSeatsInRow.value"
              :mapped-seat-map="seatMap.mappedSeatMap.value"
              :segment-state="seatMap.activeSegmentState.value"
              @onSeatSelect="seatMap.selectSeat"
              @onUnselectSeat="seatMap.unselectSeat"
            />
          </div>
          <NoSeatMapForSegment v-else>
            <template #default>
              <FreeButton
                type="button"
                class="green-main-button"
                @click="skipSegment"
              >
                {{ skipSegmentButtonText }}
              </FreeButton>
            </template>
          </NoSeatMapForSegment>
        </div>
        <div
          v-if="seatMap.mappedSeatMap.value"
          class="bg-background-neutral-secondary fixed bottom-0 w-full space-y-4 rounded-t-2xl py-4"
        >
          <SeatMapPassengerSelect
            class="px-4"
            :passengers-keys="passengersKeys"
            :passengers-data="passengersData"
            :active-passenger-key="seatMap.activePassengerKey.value"
            :passengers-seat-state="seatMap.activeSegmentState.value"
            @onPassengerSelect="seatMap.selectPassenger"
            @onPassengerSeatUnselect="seatMap.unselectSeat"
          />
          <SeatMapPriceBlock
            class="max-w-[100vw] px-4"
            :passengers-keys="passengersKeys"
            qa-id="seatMap.map.continue"
            :seat-map-state="seatMap.draftSeatMapStateByFlights.value"
            @onContinue="continueSelectSeat"
          />
        </div>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import {
    FreeBottomSheet,
    FreeCloseButton,
    FreeHeader,
    FreeButton,
    type flightEntity,
  } from 'freedom-shared';
  import { controllerModel } from 'shared';
  import { isEqual } from 'lodash-es';
  import type { QueryObserverResult } from '@tanstack/vue-query';
  import type { AxiosResponse } from 'axios';
  import type { Airflow } from '@aviata/platform-types';
  import type { PassengerKey, PassengersData } from '@aviata/booking-data';
  import { useI18n } from 'vue-i18n';

  import SegmentDetails from './SegmentDetails.vue';
  import SeatMapPriceBlock from '../SeatMapPriceBlock.vue';
  import { useDraftSeatMap } from '../model';
  import { seatMapLib } from '../lib';
  import type { SelectedSeatsState } from '../types';

  import {
    FlightSelectButton,
    SeatMapLegend,
    SeatMapPassengerSelect,
    SeatMapLoader,
    SeatMapSchema,
    NoSeatMapForSegment,
    SeatMapSegmentError,
  } from './index';

  const props = defineProps<{
    name: string;
    flight: flightEntity.types.MappedFlight;
    seatMapQueries: QueryObserverResult<
      AxiosResponse<Airflow.SeatMap.Entity>
    >[];
    passengersData: PassengersData;
    seatMapStateByFlight: SelectedSeatsState;
    passengersKeys: PassengerKey[];
  }>();

  const emit = defineEmits<{
    onCommitState: [SelectedSeatsState];
    onTryToClose: [];
  }>();

  const { t } = useI18n();
  const seatMap = useDraftSeatMap({
    flight: props.flight,
    seatMapStateByFlight: computed(() => props.seatMapStateByFlight),
    passengersData: props.passengersData,
    seatMapQueries: computed(() => props.seatMapQueries),
    passengersKeys: props.passengersKeys,
  });
  const bottomSheet = controllerModel.useBottomSheet();
  const isDraftStateHasChanges = computed(
    () =>
      !isEqual(
        props.seatMapStateByFlight,
        seatMap.draftSeatMapStateByFlights.value,
      ),
  );
  const isLastSegment = computed(
    () => seatMap.activeSegmentIdx.value + 1 === props.flight.segments.length,
  );
  const flightSelectBlockRef = ref();
  const skipSegmentButtonText = computed(() => {
    return isLastSegment.value
      ? t('ancillaries.seatMap.noSeatMapForSegment.commit')
      : t('ancillaries.seatMap.noSeatMapForSegment.proceedToNextSegment');
  });

  function init() {
    seatMap.selectSegment(0);
    seatMap.selectPassenger(props.passengersKeys[0]);
    seatMap.resetState();
  }

  function continueSelectSeat() {
    const nextSegmentIdx = seatMap.activeSegmentIdx.value + 1;
    const nextPassenger = getNextPassenger();
    // should select next passenger if there is
    if (nextPassenger) {
      seatMap.selectPassenger(nextPassenger);
    } else if (props.flight.segments.length !== nextSegmentIdx) {
      // should switch to next segment if there is
      seatMap.selectSegment(nextSegmentIdx);
      seatMap.selectPassenger(props.passengersKeys[0]);
      scrollToTop();
      commitState();
    } else {
      commitAndCloseBottomSheet();
    }
  }

  function getNextPassenger() {
    const activePassengerIdx = props.passengersKeys.findIndex(
      key => seatMap.activePassengerKey.value === key,
    );
    return props.passengersKeys[activePassengerIdx + 1];
  }

  function hasSegmentCommittedSeats(segmentId: number) {
    return seatMapLib.hasSegmentSeats({
      seatMapState: props.seatMapStateByFlight[segmentId],
      passengersKeys: props.passengersKeys,
    });
  }

  function commitState() {
    emit('onCommitState', [...seatMap.draftSeatMapStateByFlights.value]);
  }

  function commitAndCloseBottomSheet() {
    commitState();
    bottomSheet.hide(props.name);
  }

  function scrollToTop() {
    flightSelectBlockRef.value?.scrollIntoView(true);
  }

  function skipSegment() {
    if (isLastSegment.value) {
      commitAndCloseBottomSheet();
    } else {
      seatMap.selectSegment(seatMap.activeSegmentIdx.value + 1);
    }
  }
</script>
